import { marked } from 'marked';
import type { Document } from './citation-utils';
import {
  buildCitationNumberingMap,
  CITATION_EVIDENCE_MARKER_PATTERN,
  filterInvalidCitations,
  parseCitationMarker,
} from './citation-utils';

export { buildCitationNumberingMap } from './citation-utils';

/**
 * Shared citation rendering logic for case overview and matter memory components
 * Converts markdown content with [cite:N] citations to HTML with clickable citation buttons
 */

interface CitationSource {
  documentGuid: string;
}

const evidenceCitationPattern = () => new RegExp(CITATION_EVIDENCE_MARKER_PATTERN, 'gi');

export interface CitationRenderOptions {
  /** Map of citation numbers to source metadata */
  sources: Map<string, CitationSource>;
  /** Available documents for name lookup */
  documents?: Document[];
  /** Content to process */
  content: string;
  /** Matter slug for building document URLs */
  slug?: string;
  /** Optional pre-computed citation numbers map (for consistent numbering across sections) */
  citationNumbers?: Map<string, number>;
}

/**
 * Process markdown content and replace citations with clickable buttons
 * Handles multiple formats: [cite:GUID:chunkId], [cite:GUID:sN], [cite:GUID], and [cite:N]
 */
export function renderContentWithCitations({
  content,
  sources,
  documents,
  slug,
  citationNumbers: providedCitationNumbers,
}: CitationRenderOptions): string {
  // Use provided citation numbers or build them from content
  const citationNumbers = providedCitationNumbers || buildCitationNumberingMap(content);

  // Convert markdown to HTML first
  let html = marked(content, { gfm: true, breaks: true }) as string;

  // Now remove invalid citations from the HTML
  // TODO: We should update our citation utils to handle more citation formats, e.g., range formats like [cite:GUID:5-9].
  html = filterInvalidCitations(html, documents);

  // Replace chunk and segment evidence citations with clickable links.
  // Note: Don't add title attribute - useCitationTooltips will fetch and add enhanced tooltip data
  html = html.replace(evidenceCitationPattern(), (marker) => {
    const citation = parseCitationMarker(marker);
    if (!citation || (citation.kind !== 'chunk' && citation.kind !== 'segment')) return '';
    const displayNumber = citationNumbers.get(citation.key) || '?';
    const isSegment = citation.kind === 'segment';
    const queryKey = isSegment ? 'segment' : 'chunk';
    const dataAttribute = isSegment ? 'data-segment-index' : 'data-chunk-id';
    const cellRange = citation.kind === 'chunk' ? citation.cellRange : undefined;

    const href = slug
      ? `/matter/${slug}/f/${citation.documentGuid}?from=overview&${queryKey}=${citation.index}${cellRange ? `&cellRange=${encodeURIComponent(cellRange)}` : ''}`
      : `#cite-${citation.key.replaceAll(':', '-')}`;
    return ` <a href="${href}" class="citation-link" data-doc-guid="${citation.documentGuid}" ${dataAttribute}="${citation.index}"${cellRange ? ` data-cell-range="${cellRange}"` : ''}>${displayNumber}</a> `;
  });

  // Replace [cite:GUID] with clickable links (document-level)
  // Add data-file-name for enhanced tooltip (no page/paragraph info for document-level citations)
  html = html.replace(/\[cite:([a-f0-9-]{36})\](?!:)/g, (_, guid) => {
    const displayNumber = citationNumbers.get(guid) || '?';
    const doc = documents?.find(d => d.guid === guid);
    const fileName = doc?.displayName || doc?.fileName || '';

    const href = slug
      ? `/matter/${slug}/f/${guid}?from=overview`
      : `#cite-${guid}`;
    return ` <a href="${href}" class="citation-link" data-doc-guid="${guid}"${fileName ? ` data-file-name="${fileName}"` : ''}>${displayNumber}</a> `;
  });

  // Legacy: Replace [cite:N] with clickable links (backward compatibility)
  html = html.replace(/\[cite:(\d+)\]/g, (_, num) => {
    const source = sources.get(num);
    if (!source) return ''; // Remove unrecognized citations

    const doc = documents?.find(d => d.guid === source.documentGuid);
    const title = doc?.displayName || doc?.fileName || '';
    const href = slug
      ? `/matter/${slug}/f/${source.documentGuid}?from=overview&cite=${num}`
      : `#cite-${num}`;
    return ` <a href="${href}" class="citation-link" data-cite="${num}" data-doc-guid="${source.documentGuid}"${title ? ` title="${title}"` : ''}>${num}</a> `;
  });

  return html;
}

/**
 * Parse markdown sections, stopping at the Citations section
 * Used by overview and matter-memory components
 */
export interface MarkdownSection {
  id: string;
  title: string;
  content: string;
  level: number;
}

export function parseMarkdownSections(markdown: string): MarkdownSection[] {
  const lines = markdown.split('\n');
  const sections: MarkdownSection[] = [];
  let currentSection: MarkdownSection | null = null;
  let currentContent: string[] = [];
  let topLevelHeadingSkipped = false;
  let inCitationsSection = false;

  lines.forEach((line) => {
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);

    if (headingMatch) {
      const level = headingMatch[1].length;

      // Skip the first top-level heading
      if (level === 1 && !topLevelHeadingSkipped) {
        topLevelHeadingSkipped = true;
        return;
      }

      // Only create sections for level 2 headings
      if (level === 2) {
        const title = headingMatch[2].trim();

        // Stop processing if we hit Citations section header
        if (title.toLowerCase() === 'citations') {
          inCitationsSection = true;
          if (currentSection) {
            const section: MarkdownSection = currentSection;
            section.content = currentContent.join('\n').trim();
            sections.push(section);
            currentSection = null;
          }
          return;
        }

        // Save previous section
        if (currentSection) {
          const section: MarkdownSection = currentSection;
          section.content = currentContent.join('\n').trim();
          sections.push(section);
        }

        // Start new section
        const id = `section-${sections.length}`;
        currentSection = {
          id,
          title,
          content: '',
          level,
        };
        currentContent = [];
      } else if (currentSection && !inCitationsSection) {
        // Sub-headings are part of content
        currentContent.push(line);
      }
    } else if (currentSection && !inCitationsSection) {
      currentContent.push(line);
    }
  });

  // Save last section
  if (currentSection && !inCitationsSection) {
    const section: MarkdownSection = currentSection;
    section.content = currentContent.join('\n').trim();
    sections.push(section);
  }

  return sections;
}
